import React from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  MessageCircle,
  Target,
  Brain,
  Smartphone,
  TrendingUp,
  Users,
  Zap,
  CheckCircle,
  Mail,
  Calendar,
  BarChart3,
  ShoppingCart,
  Share2,
  Lightbulb } from
'lucide-react';
import { Link } from 'react-router-dom';

const AboutPage: React.FC = () => {
  return (
    <Layout data-id="qe4873dp4" data-path="src/pages/AboutPage.tsx">
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50" data-id="8j4vpwjsx" data-path="src/pages/AboutPage.tsx">
        {/* Hero Section */}
        <section className="pt-20 pb-16" data-id="lw6uqqjmi" data-path="src/pages/AboutPage.tsx">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="xlw1dz644" data-path="src/pages/AboutPage.tsx">
            <div className="text-center mb-16" data-id="qk46c7u6o" data-path="src/pages/AboutPage.tsx">
              <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-6" data-id="bdatw048o" data-path="src/pages/AboutPage.tsx">
                About Us
              </h1>
              <div className="flex items-center justify-center gap-2 mb-8" data-id="4y2ngghcu" data-path="src/pages/AboutPage.tsx">
                <span className="text-2xl" data-id="ljaf1nkg9" data-path="src/pages/AboutPage.tsx">🚀</span>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-800" data-id="xum656cj3" data-path="src/pages/AboutPage.tsx">
                  Welcome to the Future of Work – Powered by AI
                </h2>
              </div>
              <div className="max-w-4xl mx-auto text-lg text-gray-600 leading-relaxed" data-id="ngq0pti7z" data-path="src/pages/AboutPage.tsx">
                <p className="mb-4" data-id="lbwzmwzgf" data-path="src/pages/AboutPage.tsx">
                  At <span className="font-semibold text-blue-600" data-id="ak9xk06yd" data-path="src/pages/AboutPage.tsx">BotLabHQ</span>, we don't just automate — we <span className="font-semibold" data-id="63r1eresz" data-path="src/pages/AboutPage.tsx">elevate</span>. By merging powerful AI technologies with practical business strategies, we help companies grow faster, work smarter, and engage customers like never before.
                </p>
                <p className="mb-4" data-id="bvz585grt" data-path="src/pages/AboutPage.tsx">
                  Our team of technologists, designers, and AI consultants is passionate about unlocking your business's potential through intelligent automation. Whether it's your website, social platforms, or phone systems, we craft digital experiences that speak, listen, and convert — like your best employee, on their best day.
                </p>
                <p data-id="7lz64yh1t" data-path="src/pages/AboutPage.tsx">
                  From startups to enterprises, we're the tech partners behind the scenes — streamlining interactions, maximizing lead capture, and scaling your success.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-16 bg-white" data-id="w82b7dev0" data-path="src/pages/AboutPage.tsx">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="wa8m4co1y" data-path="src/pages/AboutPage.tsx">
            <div className="text-center mb-12" data-id="qx5bs472h" data-path="src/pages/AboutPage.tsx">
              <div className="flex items-center justify-center gap-2 mb-4" data-id="02eedmyyh" data-path="src/pages/AboutPage.tsx">
                <Target className="w-8 h-8 text-blue-600" data-id="5qzzw9pf6" data-path="src/pages/AboutPage.tsx" />
                <h2 className="text-3xl md:text-4xl font-bold text-gray-800" data-id="fqpb7zgsh" data-path="src/pages/AboutPage.tsx">Our Mission</h2>
              </div>
              <div className="max-w-3xl mx-auto" data-id="wabf4aal0" data-path="src/pages/AboutPage.tsx">
                <p className="text-lg text-gray-600 mb-6" data-id="t7cznpgae" data-path="src/pages/AboutPage.tsx">
                  At <span className="font-semibold text-blue-600" data-id="1x59i52ij" data-path="src/pages/AboutPage.tsx">BotLabHQ</span>, our mission is simple:
                </p>
                <div className="bg-gradient-to-r from-blue-100 to-purple-100 p-6 rounded-lg mb-6" data-id="p86sm3jyq" data-path="src/pages/AboutPage.tsx">
                  <p className="text-xl font-semibold text-gray-800 mb-4" data-id="dfdd4tf81" data-path="src/pages/AboutPage.tsx">
                    You focus on customer service — we'll handle the rest.
                  </p>
                  <p className="text-gray-600" data-id="rv1wto5xx" data-path="src/pages/AboutPage.tsx">
                    We empower businesses through intelligent automation that works around the clock. Every tool we build is designed to simplify your operations, generate leads, and drive real growth — always with your business success at heart.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What We Do Section */}
        <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50" data-id="dd6lbawgk" data-path="src/pages/AboutPage.tsx">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="hk0asy8ob" data-path="src/pages/AboutPage.tsx">
            <div className="text-center mb-12" data-id="2bh42405u" data-path="src/pages/AboutPage.tsx">
              <div className="flex items-center justify-center gap-2 mb-4" data-id="9mjvzwrt2" data-path="src/pages/AboutPage.tsx">
                <Brain className="w-8 h-8 text-purple-600" data-id="yfed6uuk5" data-path="src/pages/AboutPage.tsx" />
                <h2 className="text-3xl md:text-4xl font-bold text-gray-800" data-id="22kyhrdwl" data-path="src/pages/AboutPage.tsx">What We Do</h2>
              </div>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="tzp5tev1j" data-path="src/pages/AboutPage.tsx">
                We deliver a full-stack automation and marketing solution — building your digital presence, attracting leads, and converting them into customers using AI voice and chatbot technology.
              </p>
            </div>

            <div className="mb-12" data-id="4i3ocgkmp" data-path="src/pages/AboutPage.tsx">
              <h3 className="text-2xl font-bold text-gray-800 mb-8 text-center" data-id="jyserofw5" data-path="src/pages/AboutPage.tsx">Our Key Offerings:</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="1o9a7kp9m" data-path="src/pages/AboutPage.tsx">
                {/* AI Voice Assistants & Chatbots */}
                <Card className="hover:shadow-lg transition-shadow duration-300" data-id="d3kw3dbnz" data-path="src/pages/AboutPage.tsx">
                  <CardContent className="p-6" data-id="uz3wozibi" data-path="src/pages/AboutPage.tsx">
                    <div className="flex items-center gap-3 mb-4" data-id="redlgw0wf" data-path="src/pages/AboutPage.tsx">
                      <MessageCircle className="w-8 h-8 text-blue-600" data-id="vgm7q9d49" data-path="src/pages/AboutPage.tsx" />
                      <h4 className="text-xl font-bold text-gray-800" data-id="nrr07qymk" data-path="src/pages/AboutPage.tsx">AI Voice Assistants & Chatbots</h4>
                    </div>
                    <p className="text-gray-600" data-id="i0oyt1214" data-path="src/pages/AboutPage.tsx">
                      Smart conversational agents that handle inbound/outbound calls, customer support, lead qualification, and more — 24/7.
                    </p>
                  </CardContent>
                </Card>

                {/* AI Content Automation */}
                <Card className="hover:shadow-lg transition-shadow duration-300" data-id="gxiflrjct" data-path="src/pages/AboutPage.tsx">
                  <CardContent className="p-6" data-id="ia0x5e422" data-path="src/pages/AboutPage.tsx">
                    <div className="flex items-center gap-3 mb-4" data-id="dm9iybpd2" data-path="src/pages/AboutPage.tsx">
                      <Zap className="w-8 h-8 text-green-600" data-id="upatw9c9a" data-path="src/pages/AboutPage.tsx" />
                      <h4 className="text-xl font-bold text-gray-800" data-id="gdkpcz2d6" data-path="src/pages/AboutPage.tsx">AI Content Automation</h4>
                    </div>
                    <p className="text-gray-600" data-id="h0bombjfs" data-path="src/pages/AboutPage.tsx">
                      From long-form blogs to scroll-stopping Instagram Reels, we generate ready-to-post content that speaks your brand's voice.
                    </p>
                  </CardContent>
                </Card>

                {/* Strategic Web & App Development */}
                <Card className="hover:shadow-lg transition-shadow duration-300" data-id="lq68n49mq" data-path="src/pages/AboutPage.tsx">
                  <CardContent className="p-6" data-id="6jb4imfhr" data-path="src/pages/AboutPage.tsx">
                    <div className="flex items-center gap-3 mb-4" data-id="eh01q5mdg" data-path="src/pages/AboutPage.tsx">
                      <Smartphone className="w-8 h-8 text-purple-600" data-id="a9q58nv0y" data-path="src/pages/AboutPage.tsx" />
                      <h4 className="text-xl font-bold text-gray-800" data-id="q29wtkkpy" data-path="src/pages/AboutPage.tsx">Strategic Web & App Development</h4>
                    </div>
                    <p className="text-gray-600" data-id="v6dm1q0at" data-path="src/pages/AboutPage.tsx">
                      High-performing websites and Android apps designed to deliver fast, smooth, and engaging user experiences.
                    </p>
                  </CardContent>
                </Card>

                {/* Smart Lead Capture & Nurturing */}
                <Card className="hover:shadow-lg transition-shadow duration-300" data-id="0k51x4pat" data-path="src/pages/AboutPage.tsx">
                  <CardContent className="p-6" data-id="a7xcbqius" data-path="src/pages/AboutPage.tsx">
                    <div className="flex items-center gap-3 mb-4" data-id="k20aj8gh8" data-path="src/pages/AboutPage.tsx">
                      <Users className="w-8 h-8 text-orange-600" data-id="v2zih4cuk" data-path="src/pages/AboutPage.tsx" />
                      <h4 className="text-xl font-bold text-gray-800" data-id="ozj6ndso0" data-path="src/pages/AboutPage.tsx">Smart Lead Capture & Nurturing</h4>
                    </div>
                    <p className="text-gray-600" data-id="tvhz15qdh" data-path="src/pages/AboutPage.tsx">
                      Capture inquiries from calls, emails, and forms — then automatically confirm, follow-up, and convert them with smart automation.
                    </p>
                  </CardContent>
                </Card>

                {/* Targeted Marketing Campaigns */}
                <Card className="hover:shadow-lg transition-shadow duration-300" data-id="t1lmbfyyz" data-path="src/pages/AboutPage.tsx">
                  <CardContent className="p-6" data-id="f3vhhhghe" data-path="src/pages/AboutPage.tsx">
                    <div className="flex items-center gap-3 mb-4" data-id="9wtz64pes" data-path="src/pages/AboutPage.tsx">
                      <TrendingUp className="w-8 h-8 text-red-600" data-id="ltlphatrd" data-path="src/pages/AboutPage.tsx" />
                      <h4 className="text-xl font-bold text-gray-800" data-id="siunzvyv3" data-path="src/pages/AboutPage.tsx">Targeted Marketing Campaigns</h4>
                    </div>
                    <p className="text-gray-600" data-id="m3qf3ugbn" data-path="src/pages/AboutPage.tsx">
                      We launch and manage data-driven campaigns on Facebook, Instagram, LinkedIn, and YouTube to bring in qualified leads.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Voice-Driven Philosophy */}
        <section className="py-16 bg-white" data-id="lrek31wgf" data-path="src/pages/AboutPage.tsx">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="frlq6bkxp" data-path="src/pages/AboutPage.tsx">
            <div className="text-center mb-12" data-id="xibdpknni" data-path="src/pages/AboutPage.tsx">
              <div className="flex items-center justify-center gap-2 mb-4" data-id="7kw9mu8gn" data-path="src/pages/AboutPage.tsx">
                <span className="text-3xl" data-id="wt4mq8tpl" data-path="src/pages/AboutPage.tsx">🎤</span>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-800" data-id="vzr2zxxla" data-path="src/pages/AboutPage.tsx">Our Voice-Driven Philosophy</h2>
              </div>
            </div>

            <div className="max-w-4xl mx-auto" data-id="wpnil3fel" data-path="src/pages/AboutPage.tsx">
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-8 rounded-lg mb-8" data-id="39mmk9k3d" data-path="src/pages/AboutPage.tsx">
                <p className="text-lg text-gray-700 mb-4" data-id="0s4hi7s45" data-path="src/pages/AboutPage.tsx">
                  Our AI voice agents are designed to speak like a helpful human — warm, clear, and intuitive.
                </p>
                <p className="text-lg text-gray-700" data-id="46obgiio7" data-path="src/pages/AboutPage.tsx">
                  They don't just answer — they <span className="font-semibold" data-id="px6fku7fe" data-path="src/pages/AboutPage.tsx">engage</span>.
                </p>
              </div>

              <div className="mb-8" data-id="61apjifmx" data-path="src/pages/AboutPage.tsx">
                <p className="text-lg text-gray-600 mb-4" data-id="fwxyv5x6p" data-path="src/pages/AboutPage.tsx">
                  Here's how they might start a conversation in your industry:
                </p>
                <div className="space-y-4" data-id="m20dkzu34" data-path="src/pages/AboutPage.tsx">
                  <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg" data-id="xtegxmjlw" data-path="src/pages/AboutPage.tsx">
                    <span className="text-2xl" data-id="52rc7929a" data-path="src/pages/AboutPage.tsx">🏠</span>
                    <p className="text-gray-700" data-id="5ixps4fch" data-path="src/pages/AboutPage.tsx">"Looking for help managing property inquiries or scheduling inspections?"</p>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg" data-id="c4nvcko8y" data-path="src/pages/AboutPage.tsx">
                    <span className="text-2xl" data-id="mmb6nxu1l" data-path="src/pages/AboutPage.tsx">🛍️</span>
                    <p className="text-gray-700" data-id="s3gn4dp02" data-path="src/pages/AboutPage.tsx">"Want to turn browsing customers into paying shoppers automatically?"</p>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg" data-id="8ws2k86il" data-path="src/pages/AboutPage.tsx">
                    <span className="text-2xl" data-id="jjvqknzz6" data-path="src/pages/AboutPage.tsx">💼</span>
                    <p className="text-gray-700" data-id="6piq5pnzo" data-path="src/pages/AboutPage.tsx">"Would you like our store agent to act as an AI assistant that talks to clients for you?"</p>
                  </div>
                </div>
              </div>

              <p className="text-lg text-gray-600 text-center" data-id="xxqvbtra4" data-path="src/pages/AboutPage.tsx">
                Our goal is to simplify every touchpoint — whether for real estate, retail, or customer service teams.
              </p>
            </div>
          </div>
        </section>

        {/* Tech Integration */}
        <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50" data-id="y07d3ah1e" data-path="src/pages/AboutPage.tsx">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="odfgwe1k8" data-path="src/pages/AboutPage.tsx">
            <div className="text-center mb-12" data-id="x1r8fzr2y" data-path="src/pages/AboutPage.tsx">
              <div className="flex items-center justify-center gap-2 mb-4" data-id="28ogl7fyl" data-path="src/pages/AboutPage.tsx">
                <Lightbulb className="w-8 h-8 text-yellow-600" data-id="fnx8t8aig" data-path="src/pages/AboutPage.tsx" />
                <h2 className="text-3xl md:text-4xl font-bold text-gray-800" data-id="ox9kqn5yw" data-path="src/pages/AboutPage.tsx">Tech That Works With You</h2>
              </div>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="tfm8ceciw" data-path="src/pages/AboutPage.tsx">
                We integrate with your favorite tools so your business doesn't have to change a thing — it just gets smarter:
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-8 mb-8" data-id="w2rsv7ggp" data-path="src/pages/AboutPage.tsx">
              <Card className="text-center p-6" data-id="7zi6v5f9i" data-path="src/pages/AboutPage.tsx">
                <Mail className="w-12 h-12 text-blue-600 mx-auto mb-4" data-id="thia8vuau" data-path="src/pages/AboutPage.tsx" />
                <h4 className="font-bold text-gray-800 mb-2" data-id="vapvlmd3x" data-path="src/pages/AboutPage.tsx">Messaging & Email</h4>
                <p className="text-sm text-gray-600" data-id="jw533t7iw" data-path="src/pages/AboutPage.tsx">WhatsApp, Gmail, Outlook</p>
              </Card>

              <Card className="text-center p-6" data-id="i1wjy5pw3" data-path="src/pages/AboutPage.tsx">
                <Calendar className="w-12 h-12 text-green-600 mx-auto mb-4" data-id="h4x4glavm" data-path="src/pages/AboutPage.tsx" />
                <h4 className="font-bold text-gray-800 mb-2" data-id="pkwl68569" data-path="src/pages/AboutPage.tsx">Data & Scheduling</h4>
                <p className="text-sm text-gray-600" data-id="7kjh6cc1v" data-path="src/pages/AboutPage.tsx">Google Sheets, Google Calendar, Airtable</p>
              </Card>

              <Card className="text-center p-6" data-id="amqrtr1u6" data-path="src/pages/AboutPage.tsx">
                <BarChart3 className="w-12 h-12 text-purple-600 mx-auto mb-4" data-id="z4jxqn5t2" data-path="src/pages/AboutPage.tsx" />
                <h4 className="font-bold text-gray-800 mb-2" data-id="jm82ybucy" data-path="src/pages/AboutPage.tsx">CRM & Automation</h4>
                <p className="text-sm text-gray-600" data-id="exzu7zwle" data-path="src/pages/AboutPage.tsx">Slack, Mailchimp, HubSpot, Monday.com, Zoho CRM, Salesforce</p>
              </Card>

              <Card className="text-center p-6" data-id="ypfbqltgg" data-path="src/pages/AboutPage.tsx">
                <ShoppingCart className="w-12 h-12 text-orange-600 mx-auto mb-4" data-id="a4u0gc6o7" data-path="src/pages/AboutPage.tsx" />
                <h4 className="font-bold text-gray-800 mb-2" data-id="yl7vfhkui" data-path="src/pages/AboutPage.tsx">Finance & E-commerce</h4>
                <p className="text-sm text-gray-600" data-id="u9hr0yaap" data-path="src/pages/AboutPage.tsx">Shopify, QuickBooks, Xero</p>
              </Card>

              <Card className="text-center p-6" data-id="2igza98wx" data-path="src/pages/AboutPage.tsx">
                <Share2 className="w-12 h-12 text-blue-600 mx-auto mb-4" data-id="5s4j7ljbm" data-path="src/pages/AboutPage.tsx" />
                <h4 className="font-bold text-gray-800 mb-2" data-id="vp7npo7vx" data-path="src/pages/AboutPage.tsx">Marketing & Social</h4>
                <p className="text-sm text-gray-600" data-id="x6rk91p2n" data-path="src/pages/AboutPage.tsx">Facebook, Instagram, YouTube, LinkedIn</p>
              </Card>
            </div>

            <div className="text-center" data-id="vl6y946bi" data-path="src/pages/AboutPage.tsx">
              <p className="text-lg text-gray-700 font-medium" data-id="q3hg0gn9m" data-path="src/pages/AboutPage.tsx">
                From WhatsApp call replies to resume scanning, we bring AI to the tools you already trust.
              </p>
            </div>
          </div>
        </section>

        {/* Our Promise */}
        <section className="py-16 bg-white" data-id="nnv0ku7qz" data-path="src/pages/AboutPage.tsx">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="7953jm6hz" data-path="src/pages/AboutPage.tsx">
            <div className="text-center mb-12" data-id="55v9mzz1h" data-path="src/pages/AboutPage.tsx">
              <div className="flex items-center justify-center gap-2 mb-4" data-id="8jsnjqq5k" data-path="src/pages/AboutPage.tsx">
                <span className="text-3xl" data-id="l8dymztwe" data-path="src/pages/AboutPage.tsx">💡</span>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-800" data-id="t5xq79fhx" data-path="src/pages/AboutPage.tsx">Our Promise</h2>
              </div>
            </div>

            <div className="max-w-4xl mx-auto" data-id="7v1p82x03" data-path="src/pages/AboutPage.tsx">
              <div className="bg-gradient-to-r from-blue-100 to-purple-100 p-8 rounded-lg mb-8" data-id="fk5n444xy" data-path="src/pages/AboutPage.tsx">
                <p className="text-lg text-gray-700 mb-4" data-id="4m0hrfeml" data-path="src/pages/AboutPage.tsx">
                  At <span className="font-semibold text-blue-600" data-id="5ticq6567" data-path="src/pages/AboutPage.tsx">BotLabHQ</span>, our promise is simple:
                </p>
                <p className="text-xl font-bold text-gray-800 mb-4" data-id="d7awtvk5d" data-path="src/pages/AboutPage.tsx">
                  We create intelligent systems that drive real-world results.
                </p>
                <p className="text-lg text-gray-700" data-id="8z2coxnq7" data-path="src/pages/AboutPage.tsx">
                  Every solution we build — whether it's a voice agent, a marketing campaign, or a custom workflow — is crafted with one goal in mind: <span className="font-semibold" data-id="a915uwk5d" data-path="src/pages/AboutPage.tsx">to help your business grow with less manual effort and more meaningful impact</span>.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8 mb-8" data-id="a0lkqhmyt" data-path="src/pages/AboutPage.tsx">
                <div className="space-y-4" data-id="pv1zozb8v" data-path="src/pages/AboutPage.tsx">
                  <div className="flex items-start gap-3" data-id="5jjtuvhld" data-path="src/pages/AboutPage.tsx">
                    <CheckCircle className="w-6 h-6 text-green-600 mt-1" data-id="s61vc5xx3" data-path="src/pages/AboutPage.tsx" />
                    <p className="text-gray-700" data-id="nmc40n2q7" data-path="src/pages/AboutPage.tsx">Cutting-edge automation with thoughtful design and smart strategy</p>
                  </div>
                  <div className="flex items-start gap-3" data-id="k6s1xg21c" data-path="src/pages/AboutPage.tsx">
                    <CheckCircle className="w-6 h-6 text-green-600 mt-1" data-id="f0ecyva1g" data-path="src/pages/AboutPage.tsx" />
                    <p className="text-gray-700" data-id="h2e2e7e6n" data-path="src/pages/AboutPage.tsx">Tools that don't just work — they work <em data-id="umr2hccjc" data-path="src/pages/AboutPage.tsx">for you</em></p>
                  </div>
                  <div className="flex items-start gap-3" data-id="fgmw0e4t1" data-path="src/pages/AboutPage.tsx">
                    <CheckCircle className="w-6 h-6 text-green-600 mt-1" data-id="7gwqrupez" data-path="src/pages/AboutPage.tsx" />
                    <p className="text-gray-700" data-id="ht74vy91z" data-path="src/pages/AboutPage.tsx">Less time chasing leads, more time with customers</p>
                  </div>
                </div>
                <div className="space-y-4" data-id="bvmupl6me" data-path="src/pages/AboutPage.tsx">
                  <div className="flex items-start gap-3" data-id="wxqh69ege" data-path="src/pages/AboutPage.tsx">
                    <CheckCircle className="w-6 h-6 text-green-600 mt-1" data-id="4f342ezz8" data-path="src/pages/AboutPage.tsx" />
                    <p className="text-gray-700" data-id="13yheapho" data-path="src/pages/AboutPage.tsx">No more chasing follow-ups or managing complexity</p>
                  </div>
                  <div className="flex items-start gap-3" data-id="cplaiylxo" data-path="src/pages/AboutPage.tsx">
                    <CheckCircle className="w-6 h-6 text-green-600 mt-1" data-id="r2avhkz4n" data-path="src/pages/AboutPage.tsx" />
                    <p className="text-gray-700" data-id="k4qakpy68" data-path="src/pages/AboutPage.tsx">Because at the end of the day, AI's not about tech</p>
                  </div>
                  <div className="flex items-start gap-3" data-id="3z03wa4i6" data-path="src/pages/AboutPage.tsx">
                    <CheckCircle className="w-6 h-6 text-green-600 mt-1" data-id="l3advn4c8" data-path="src/pages/AboutPage.tsx" />
                    <p className="text-gray-700" data-id="mxsrxeflp" data-path="src/pages/AboutPage.tsx"><span className="font-semibold" data-id="etxmxwfoo" data-path="src/pages/AboutPage.tsx">Smarter systems. Simpler processes. Stronger growth.</span></p>
                  </div>
                </div>
              </div>

              <p className="text-center text-lg font-medium text-gray-800" data-id="fhptrckp9" data-path="src/pages/AboutPage.tsx">
                That's the BotLabHQ promise.
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600" data-id="h4qrt6kdl" data-path="src/pages/AboutPage.tsx">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="5np8lsp9v" data-path="src/pages/AboutPage.tsx">
            <div className="flex items-center justify-center gap-2 mb-4" data-id="531m5cxrp" data-path="src/pages/AboutPage.tsx">
              <span className="text-3xl" data-id="mja6i1uq1" data-path="src/pages/AboutPage.tsx">💼</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white" data-id="ekvdeb37g" data-path="src/pages/AboutPage.tsx">Ready to Explore What's Possible?</h2>
            </div>
            <p className="text-xl text-blue-100 mb-8" data-id="1p8cv3w1f" data-path="src/pages/AboutPage.tsx">Let's talk.</p>
            <p className="text-lg text-blue-100 mb-8" data-id="cijo0l9eo" data-path="src/pages/AboutPage.tsx">
              Our AI agent is online now. Or you can book a demo, send a message, and take the first step into your company's future.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="xm6hauhnx" data-path="src/pages/AboutPage.tsx">
              <Button asChild size="lg" className="bg-white text-blue-600 hover:bg-blue-50" data-id="1qr1o6rw0" data-path="src/pages/AboutPage.tsx">
                <Link to="/contact" data-id="91s0br4p0" data-path="src/pages/AboutPage.tsx">Get Started Today</Link>
              </Button>
              <Button asChild size="lg" className="bg-white text-blue-600 hover:bg-blue-50" data-id="je3dkt8ky" data-path="src/pages/AboutPage.tsx">
                <Link to="/use-cases" data-id="getgkagbs" data-path="src/pages/AboutPage.tsx">Explore Use Cases</Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </Layout>);

};

export default AboutPage;